import styled from "@emotion/styled";
import { CardContainer } from "../ui/cardContainer";
import { Logo } from "../ui/logo";
import { ErrorMessage, Input } from "../ui/input";
import React, { useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../ui/button";
import logo from "../img/logo.png";
import { Formik, FormikProps, FormikErrors, FormikTouched } from "formik";
import { initPaymentSession } from "../api/payment";
import { Main } from "../layout";
import * as Yup from 'yup';

interface FormProps {
    amount: number;
}

const AMOUNT_LIMITS = {
    MIN: 1,
    MAX: 9999999999,
    DEFAULT: 0,
} as const;

const ERROR_MESSAGES = {
    REQUIRED: 'Сумма пополнения не может быть пустой',
    INTEGER: 'Сумма должна быть целым числом',
    MIN: `Сумма должна быть не менее ${AMOUNT_LIMITS.MIN} рублей`,
    MAX: 'Сумма слишком большая',
    SERVER: 'Ошибка на сервере, повторите позже',
} as const;

const ERROR_DISPLAY_DURATION = 3000;

const validationSchema = Yup.object().shape({
    amount: Yup.number()
        .required(ERROR_MESSAGES.REQUIRED)
        .integer(ERROR_MESSAGES.INTEGER)
        .min(AMOUNT_LIMITS.MIN, ERROR_MESSAGES.MIN)
        .max(AMOUNT_LIMITS.MAX, ERROR_MESSAGES.MAX)
});


const sanitizeNumericInput = (value: string): string => {
    let sanitized = value.replace(/[^0-9]/g, '');

    if (sanitized.startsWith("0")) {
        sanitized = sanitized.slice(1);
    }

    return sanitized;
};

const showTemporaryError = (
    formikRef: React.RefObject<FormikProps<FormProps>>,
    errorMessage: string
): void => {
    formikRef.current?.setTouched({ amount: true });
    formikRef.current?.setErrors({ amount: errorMessage });

    setTimeout(() => {
        formikRef.current?.setErrors({});
    }, ERROR_DISPLAY_DURATION);
};


const useAutoFocus = (ref: React.RefObject<HTMLInputElement>) => {
    useEffect(() => {
        ref.current?.focus();
    }, [ref]);
};

const usePaymentHandlers = (
    formikRef: React.RefObject<FormikProps<FormProps>>,
    orderId?: string
) => {
    const handlePaymentSubmit = useCallback(async (values: FormProps) => {
        try {
            const { payment_url } = await initPaymentSession(values.amount, orderId);
            window.location.replace(payment_url);
        } catch (error) {
            console.error('Payment initialization failed:', error);
            showTemporaryError(formikRef, ERROR_MESSAGES.SERVER);
        }
    }, [formikRef, orderId]);

    const handleAmountChange = useCallback(async (
        e: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: (field: string, value: number | string) => void
    ) => {
        const { value } = e.target;
        const sanitizedValue = sanitizeNumericInput(value);

        setFieldValue('amount', sanitizedValue === '' ? 0 : sanitizedValue);
    }, []);

    return { handlePaymentSubmit, handleAmountChange };
};

const PayerHeader = () => (
    <InfoContainer>
        <Logo>
            <img src={logo} alt="logo"/>
        </Logo>
        <TextTitle>Сумма заказа</TextTitle>
    </InfoContainer>
);

const AmountInput = ({
                         inputRef,
                         values,
                         errors,
                         touched,
                         handleBlur,
                         handleAmountChange,
                         setFieldValue,
                     }: {
    inputRef: React.RefObject<HTMLInputElement>;
    values: FormProps;
    errors: FormikErrors<FormProps>;
    touched: FormikTouched<FormProps>;
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleAmountChange: (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: (field: string, value: number | string) => void) => void;
    setFieldValue: (field: string, value: number | string) => void;
}) => (
    <>
        {errors.amount && touched.amount && (
            <ErrorMessage>{errors.amount}</ErrorMessage>
        )}
        <Input
            ref={inputRef}
            placeholder="Введите сумму"
            type="text"
            inputMode="decimal"
            pattern="[0-9]+([\.][0-9]+)?"
            name="amount"
            onChange={(e) => handleAmountChange(e, setFieldValue)}
            maxLength={9}
            value={values.amount}
            onBlur={handleBlur}
            error={Boolean(errors.amount && touched.amount)}
        />
    </>
);

export const Payer = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const initialAmount = AMOUNT_LIMITS.DEFAULT;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const formikRef = useRef<FormikProps<FormProps>>(null);

    const { handlePaymentSubmit, handleAmountChange } =
        usePaymentHandlers(formikRef, orderId);

    useAutoFocus(inputRef);

    return (
        <Main>
            <CardContainer>
                <PayerHeader />
                <ButtonAndInputContainer>
                    <Formik
                        innerRef={formikRef}
                        initialValues={{ amount: initialAmount }}
                        validationSchema={validationSchema}
                        validateOnBlur={false}
                        onSubmit={handlePaymentSubmit}
                        enableReinitialize
                    >
                        {({ values, errors, touched, handleBlur, handleSubmit, setFieldValue }) => (
                            <InputForm onSubmit={handleSubmit}>
                                <AmountInput
                                    inputRef={inputRef}
                                    values={values}
                                    errors={errors}
                                    touched={touched}
                                    handleBlur={handleBlur}
                                    handleAmountChange={handleAmountChange}
                                    setFieldValue={setFieldValue}
                                />
                            </InputForm>
                        )}
                    </Formik>
                    <Button onClick={() => formikRef.current?.submitForm()}>
                        Оплатить
                    </Button>
                </ButtonAndInputContainer>
            </CardContainer>
        </Main>
    );
};

const ButtonAndInputContainer = styled.div`
    display: flex;
    justify-items: center;
    align-items: center;
    flex-direction: column;
    gap: 20px;
    width: 100%;
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const TextTitle = styled.p`
    font-size: 22px;
    text-align: center;
    margin: 0;
    color: #2f2410;
    font-family: "Lato", sans-serif;
    font-weight: 400;
`;

const InputForm = styled.form`
    display: flex;
    flex-direction: column;
    align-content: center;
    gap: 0.8vw;
    width: 100%;
`;